import {
  ApiReport,
  ApiReportStep,
  ApiReportStepStatus,
} from '../types/models.types';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import type { OrderTimelineDto } from '../types/timeline.types';

/**
 * @param isoDate - Data w formacie ISO lub undefined
 * @returns Data w formacie DD.MM.YYYY
 */
export const formatDateToDDMMYYYY = (isoDate?: string): string => {
  if (!isoDate) {
    return 'NaN';
  }

  return format(new Date(isoDate), 'dd.MM.yyyy', { locale: pl });
};

const mapStatusToStepStatus = (
  status: OrderTimelineDto['orderTimelineStatus'],
): ApiReportStepStatus => {
  if (!status) {
    return 'krok 1';
  }

  const statusMap: Record<
    Exclude<OrderTimelineDto['orderTimelineStatus'], undefined>,
    ApiReportStepStatus
  > = {
    PENDING_ACCEPTANCE: 'krok 1',
    ACCEPTED_FOR_PROCESSING: 'krok 2',
    VEHICLE_INSPECTION_IN_PROGRESS: 'krok 3',
    REPORT_GENERATION: 'krok 4',
    REPORT_READY: 'krok 5',
    ORDER_SUSPENDED: 'krok 1',
    ORDER_CANCELED: 'krok 1',
  };

  return statusMap[status] || 'krok 1';
};

const mapStatusToStepNumber = (
  status: OrderTimelineDto['orderTimelineStatus'],
): number => {
  if (!status) {
    return 1;
  }

  const stepMap: Record<
    Exclude<OrderTimelineDto['orderTimelineStatus'], undefined>,
    number
  > = {
    PENDING_ACCEPTANCE: 1,
    ACCEPTED_FOR_PROCESSING: 2,
    VEHICLE_INSPECTION_IN_PROGRESS: 3,
    REPORT_GENERATION: 4,
    // REPORT_READY = 6 (beyond step 5) so all steps show as "Zakończone"
    REPORT_READY: 6,
    ORDER_SUSPENDED: 1,
    ORDER_CANCELED: 1,
  };

  return stepMap[status] || 1;
};

// Funkcja generująca kroki na podstawie statusu
const generateSteps = (): ApiReportStep[] => {
  const steps: ApiReportStep[] = [];

  for (let i = 1; i <= 5; i++) {
    steps.push({
      id: i,
      name: `Krok ${i}`,
      order: i,
    });
  }

  return steps;
};

const mapDisplayNameToPackageType = (
  displayName: string | undefined,
  brandType: 'RAPORT_AUTA' | 'MOTOCONTROLER' | 'MOBILEKSPERT',
  hasExpressService: boolean,
): ApiReport['package_type'] => {
  if (hasExpressService) {
    return 'Protect+';
  }

  if (displayName) {
    const normalized = displayName.trim();
    const knownTypes: ApiReport['package_type'][] = ['Basic', 'Standard', 'Protect', 'Protect+'];
    const match = knownTypes.find(
      (t) => t.toLowerCase() === normalized.toLowerCase(),
    );
    if (match) return match;
  }

  const brandMap: Record<string, ApiReport['package_type']> = {
    RAPORT_AUTA: 'Standard',
    MOTOCONTROLER: 'Protect',
    MOBILEKSPERT: 'Basic',
  };

  return brandMap[brandType] || 'Standard';
};

// Główna funkcja mappera
export const mapOrderStatusRA2ToReport = (
  orderStatus: OrderTimelineDto | undefined,
): ApiReport => {
  // Obsługa przypadku, gdy orderStatus jest undefined
  if (!orderStatus) {
    throw new Error('Nie udało się zmapować danych zamówienia');
  }

  const status = orderStatus.orderTimelineStatus || 'PENDING_ACCEPTANCE';
  const currentStep = mapStatusToStepNumber(status);
  const orderUuid = orderStatus.orderUuid || '';
  const brandType = orderStatus?.brandType || 'RAPORT_AUTA';

  // Ustalamy typ pakietu na podstawie dodatków i brandu
  const addons = orderStatus.addons || [];
  const hasExpressService = addons.some(
    (addon) => addon.type === 'EXPRESS_PACKAGE',
  );
  const reportTypeDisplayName = orderStatus.reportTypeDisplayName;
  const packageType = mapDisplayNameToPackageType(reportTypeDisplayName, brandType, hasExpressService);

  const packageDescription = reportTypeDisplayName || `Pakiet ${packageType}`;

  return {
    // Używamy externalOrderId jako numeru zamówienia widocznego dla użytkownika
    id: orderStatus.externalOrderId || orderUuid,
    created_at: formatDateToDDMMYYYY(orderStatus.createdAt),
    package_type: packageType,
    package_description: packageDescription,
    current_step: currentStep,
    current_step_status: mapStatusToStepStatus(status),
    status: orderStatus.orderTimelineStatus,
    steps: generateSteps(),
    can_change_vehicle: orderStatus.clientCanChangeVehicle || false,
    show_payment_banner: orderStatus.showPaymentBanner ? true : false,
  };
};
